import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AppDispatch } from './store';
import { cartService } from '../services/api/cart.service';
import { trackEvent } from '../utils/analytics';
import {
  clearPersistedCartBadgeCount,
  countCartTotalQuantity,
  readPersistedCartBadgeCount,
  shouldFetchCartSummary,
  writePersistedCartBadgeCount,
} from '../utils/cartBadge';

export const GUEST_CART_ACTIVITY_KEY = 'cart_guest_activity';

const CART_SUMMARY_TTL_MS = 45_000;
let cartSummaryFetchedAt = 0;
let cartSummaryInFlight: Promise<CartSummaryResponse> | null = null;

export interface CartSummaryResponse {
  itemCount: number;
  totalQuantity: number;
  updatedAt?: string | null;
}

export function invalidateCartSummaryCache(): void {
  cartSummaryFetchedAt = 0;
  cartSummaryInFlight = null;
}

function markGuestCartActivity(): void {
  localStorage.setItem(GUEST_CART_ACTIVITY_KEY, '1');
}

function clearGuestCartActivity(): void {
  localStorage.removeItem(GUEST_CART_ACTIVITY_KEY);
}

function hasGuestCartActivity(): boolean {
  return localStorage.getItem(GUEST_CART_ACTIVITY_KEY) === '1';
}

function isCartPayload(data: unknown): data is Cart {
  return (
    !!data &&
    typeof data === 'object' &&
    Array.isArray((data as Cart).items)
  );
}

export interface CartItem {
  id: string;
  designId: string;
  productId: string;
  productVariantId: string;
  selectedColor?: string;
  selectedColorLabel?: string | null;
  selectedSize?: string | null;
  selectionSnapshot?: {
    size?: string | null;
    sizeLabel?: string | null;
    colorHex?: string | null;
    colorLabel?: string | null;
    fit?: 'regular' | 'oversize' | null;
    gender?: 'men' | 'women' | 'unisex' | null;
    displayMockupFront?: string | null;
      displayMockupBack?: string | null;
      sku?: string | null;
      variantId?: string | null;
      designDraftId?: string | null;
      selectedSides?: string[] | null;
      printTemplateId?: string | null;
      designVersion?: number | null;
    };
  designAcquisition?: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  design: any;
  product: any;
  productVariant: any;
  createdAt: string;
}

export interface Cart {
  id: string;
  items: CartItem[];
  createdAt: string;
  updatedAt: string;
}

interface CartState {
  cart: Cart | null;
  sessionId: string;
  /** Persisted navbar badge when full cart is not hydrated. */
  badgeCount: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

function applyBadgeCount(state: CartState, count: number): void {
  const safe = Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0;
  state.badgeCount = safe;
  writePersistedCartBadgeCount(safe);
}

function syncBadgeFromCart(state: CartState, cart: Cart | null | undefined): void {
  applyBadgeCount(state, countCartTotalQuantity(cart?.items));
}

const readStoredGuestSessionId = (): string =>
  localStorage.getItem('cart_session_id')?.trim() || '';

/** Create a guest session id only when cart interaction needs one. */
export function ensureGuestSessionInStore(
  dispatch: AppDispatch,
  currentSessionId: string,
): string {
  if (currentSessionId) return currentSessionId;
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  dispatch(setSessionId(sessionId));
  return sessionId;
}

const initialState: CartState = {
  cart: null,
  sessionId: readStoredGuestSessionId(),
  badgeCount: readPersistedCartBadgeCount(),
  status: 'idle',
  error: null,
};

let loginCartSyncPromise: Promise<void> | null = null;
let mergeGuestCartPromise: Promise<Cart | null> | null = null;
let fetchCartInFlight = false;

export const fetchCart = createAsyncThunk<
  Cart | null,
  { force?: boolean } | void
>(
  'cart/fetch',
  async (_, { getState }) => {
    const state = getState() as any;
    const userId = state.auth?.user?.id;
    let sessionId = state.cart.sessionId;

    if (!userId && !sessionId) {
      sessionId = readStoredGuestSessionId();
    }

    // ✅ If user is authenticated, ONLY use userId (ignore sessionId)
    // This prevents fetching deleted guest carts after merge
    const finalSessionId = userId ? undefined : sessionId;
    
    console.log('[FETCH_CART] Fetching cart with:', { 
      userId, 
      sessionId: finalSessionId,
      note: userId ? 'Using userId only (authenticated)' : 'Using sessionId (guest)'
    });
    
    const response = await cartService.getCart(userId, finalSessionId);

    console.log('[FETCH_CART] Cart received:', response ? `Cart with ${response.items?.length || 0} items` : 'No cart');

    if (!response && userId) {
      console.log('[FETCH_CART] No cart found for user - this is normal for new users');
    }

    return response;
  },
  {
    condition: (options) => {
      if (options?.force) return true;
      if (fetchCartInFlight) return false;
      fetchCartInFlight = true;
      return true;
    },
  },
);

function releaseFetchCartInFlight(): void {
  fetchCartInFlight = false;
}

export const fetchCartSummary = createAsyncThunk<
  CartSummaryResponse,
  { force?: boolean } | undefined,
  { rejectValue: string }
>(
  'cart/fetchSummary',
  async (_, { getState }) => {
    const state = getState() as {
      auth: { user?: { id?: string } | null };
      cart: { sessionId: string };
    };
    const userId = state.auth?.user?.id;
    let sessionId = state.cart.sessionId;
    if (!userId && !sessionId) {
      sessionId = readStoredGuestSessionId();
    }
    const finalSessionId = userId ? undefined : sessionId;

    if (cartSummaryInFlight) {
      return cartSummaryInFlight;
    }

    cartSummaryInFlight = cartService
      .getCartSummary(finalSessionId)
      .finally(() => {
        cartSummaryInFlight = null;
        cartSummaryFetchedAt = Date.now();
      });

    return cartSummaryInFlight;
  },
  {
    condition: (options, { getState }) => {
      const state = getState() as Parameters<typeof shouldFetchCartSummary>[0];
      if (!shouldFetchCartSummary(state)) return false;
      if (options?.force) return true;
      if (cartSummaryInFlight) return false;
      if (Date.now() - cartSummaryFetchedAt < CART_SUMMARY_TTL_MS) return false;
      return true;
    },
  },
);

/** Load cart only when UI needs it and Redux has no cart payload yet. */
export const hydrateCartIfNeeded = createAsyncThunk(
  'cart/hydrateIfNeeded',
  async (_, { dispatch, getState }) => {
    const state = getState() as any;
    if (!state.auth?.user?.id) {
      ensureGuestSessionInStore(dispatch, state.cart.sessionId);
    }
    await dispatch(fetchCart());
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as { cart: CartState };
      if (state.cart.status === 'loading') return false;
      return state.cart.cart === null;
    },
  },
);

export const addToCart = createAsyncThunk(
  'cart/addItem',
  async (
    item: {
      designId: string;
      productId: string;
      productVariantId: string;
      selectedColor?: string;
      selectedColorLabel?: string;
      selectedSize?: string;
      selectedSizeLabel?: string;
      quantity: number;
      designAcquisition?: string;
      designDraftId?: string;
      selectedSides?: string[];
      frontPreviewUrl?: string | null;
      backPreviewUrl?: string | null;
      frontPrintFileUrl?: string | null;
      backPrintFileUrl?: string | null;
      printTemplateId?: string;
      designVersion?: number;
    },
    { getState, dispatch }
  ) => {
    const state = getState() as any;
    const userId = state.auth?.user?.id;
    const sessionId = userId
      ? state.cart.sessionId
      : ensureGuestSessionInStore(dispatch, state.cart.sessionId);

    console.log('[ADD_TO_CART] Adding item with color:', item.selectedColor);

    if (!userId) {
      markGuestCartActivity();
    }

    const cart = await cartService.addToCart({
      ...item,
      userId,
      sessionId,
    });

    // Track cart item added
    trackEvent('cart.item.added', {
      designId: item.designId,
      productId: item.productId,
      quantity: item.quantity,
      isGuest: !userId,
      designAcquisition: item.designAcquisition,
    });

    if (isCartPayload(cart)) {
      return cart;
    }

    const refreshed = await dispatch(fetchCart({ force: true }));
    return fetchCart.fulfilled.match(refreshed) ? refreshed.payload : null;
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateItem',
  async ({ itemId, quantity }: { itemId: string; quantity: number }, { dispatch, getState }) => {
    const state = getState() as any;
    if (!state.auth?.user?.id) {
      markGuestCartActivity();
    }
    await cartService.updateCartItem(itemId, quantity);
    const refreshed = await dispatch(fetchCart());
    return fetchCart.fulfilled.match(refreshed) ? refreshed.payload : null;
  }
);

export const removeCartItem = createAsyncThunk(
  'cart/removeItem',
  async (itemId: string, { dispatch, getState }) => {
    const state = getState() as any;
    const userId = state.auth?.user?.id;
    const sessionId = state.cart.sessionId;

    if (!userId) {
      markGuestCartActivity();
    }

    await cartService.removeCartItem(itemId, { userId, sessionId });
    // Refetch cart to get updated data
    await dispatch(fetchCart());
    return itemId;
  }
);

export const mergeGuestCart = createAsyncThunk(
  'cart/mergeGuestCart',
  async (guestSessionId: string, { dispatch }) => {
    if (mergeGuestCartPromise) {
      return mergeGuestCartPromise;
    }

    mergeGuestCartPromise = (async () => {
      console.log('[MERGE_GUEST_CART] Merging cart from sessionId:', guestSessionId);

      const data = await cartService.mergeGuestCart(guestSessionId);

      console.log('[MERGE_GUEST_CART] Merge successful');

      trackEvent('cart.merged', {
        guestSessionId,
        itemCount: data?.items?.length || 0,
      });

      if (isCartPayload(data)) {
        return data;
      }

      const refreshed = await dispatch(fetchCart());
      return fetchCart.fulfilled.match(refreshed) ? refreshed.payload : null;
    })();

    try {
      return await mergeGuestCartPromise;
    } catch (error) {
      console.error('[MERGE_GUEST_CART] Failed:', error);
      throw error;
    } finally {
      mergeGuestCartPromise = null;
    }
  },
  {
    condition: () => !mergeGuestCartPromise,
  },
);

/** Merge guest cart once after login; skips server refresh when there is no guest session. */
export const syncCartAfterLogin = createAsyncThunk(
  'cart/syncAfterLogin',
  async (_, { dispatch, getState }) => {
    if (loginCartSyncPromise) {
      await loginCartSyncPromise;
      return;
    }

    loginCartSyncPromise = (async () => {
      const guestSessionId = localStorage.getItem('cart_session_id')?.trim();
      const state = getState() as { cart: CartState };
      const hasLocalItems = (state.cart.cart?.items?.length ?? 0) > 0;
      const needsMerge = hasGuestCartActivity() || hasLocalItems;

      const clearGuestSession = () => {
        localStorage.removeItem('cart_session_id');
        clearGuestCartActivity();
        dispatch(setSessionId(''));
      };

      if (!guestSessionId) return;

      if (!needsMerge) {
        clearGuestSession();
        return;
      }

      try {
        await dispatch(mergeGuestCart(guestSessionId)).unwrap();
      } catch {
        if (needsMerge) {
          await dispatch(fetchCart()).unwrap();
        }
      } finally {
        clearGuestSession();
      }
    })().finally(() => {
      loginCartSyncPromise = null;
    });

    await loginCartSyncPromise;
  },
);

export const clearCartAfterOrder = createAsyncThunk(
  'cart/clearAfterOrder',
  async (_, { getState, dispatch }) => {
    const state = getState() as any;
    const sessionId = state.cart.sessionId;
    
    console.log('[CLEAR_CART] Clearing cart after order');
    
    try {
      await cartService.clearCart(sessionId);
      
      // Clear local state
      dispatch(clearCart());
      
      // Generate new session ID for next order
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      dispatch(setSessionId(newSessionId));
      
      console.log('✅ Cart cleared, new session created');
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  }
);

// Note: Cart persistence and merge are now handled automatically by the backend
// during logout and login operations. No client-side actions needed.

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCart: (state) => {
      state.cart = null;
      state.status = 'idle';
      state.error = null;
      state.badgeCount = 0;
      clearPersistedCartBadgeCount();
      invalidateCartSummaryCache();
    },
    setBadgeCount: (state, action: PayloadAction<number>) => {
      applyBadgeCount(state, action.payload);
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
      if (action.payload) {
        localStorage.setItem('cart_session_id', action.payload);
      } else {
        localStorage.removeItem('cart_session_id');
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cart
      .addCase(fetchCart.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        releaseFetchCartInFlight();
        state.status = 'succeeded';
        state.cart = action.payload || { id: '', items: [], createdAt: '', updatedAt: '' };
        syncBadgeFromCart(state, state.cart);
      })
      .addCase(fetchCart.rejected, (state, action) => {
        releaseFetchCartInFlight();
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch cart';
      })
      // Add to cart
      .addCase(addToCart.pending, (state, action) => {
        state.status = 'loading';
        const qty = Number(action.meta.arg.quantity) || 0;
        if (qty > 0) {
          const current =
            state.cart?.items?.length
              ? countCartTotalQuantity(state.cart.items)
              : state.badgeCount;
          applyBadgeCount(state, current + qty);
        }
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload) {
          state.cart = action.payload;
          syncBadgeFromCart(state, state.cart);
        }
        invalidateCartSummaryCache();
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to add item';
        if (state.cart?.items?.length) {
          syncBadgeFromCart(state, state.cart);
        } else {
          const qty = Number(action.meta.arg.quantity) || 0;
          applyBadgeCount(state, Math.max(0, state.badgeCount - qty));
        }
        invalidateCartSummaryCache();
      })
      // Update item
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload) {
          state.cart = action.payload;
          syncBadgeFromCart(state, state.cart);
        }
        invalidateCartSummaryCache();
      })
      // Remove item
      .addCase(removeCartItem.fulfilled, (state, action) => {
        if (state.cart) {
          state.cart.items = state.cart.items.filter(
            (item) => item.id !== action.payload
          );
          syncBadgeFromCart(state, state.cart);
        }
        invalidateCartSummaryCache();
      })
      // Merge guest cart after login
      .addCase(mergeGuestCart.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(mergeGuestCart.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload) {
          state.cart = action.payload;
          syncBadgeFromCart(state, state.cart);
        }
        invalidateCartSummaryCache();
      })
      .addCase(mergeGuestCart.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to merge cart';
      })
      .addCase(fetchCartSummary.fulfilled, (state, action) => {
        if (state.cart?.items?.length) return;
        applyBadgeCount(state, action.payload.totalQuantity ?? 0);
      });
  },
});

export const { clearCart, setSessionId, setBadgeCount } = cartSlice.actions;

// Selectors
export const selectCart = (state: any) => state.cart.cart;
export const selectCartItems = (state: any) => state.cart.cart?.items || [];
export const selectCartBadgeCount = (state: any) => state.cart.badgeCount ?? 0;
export const selectCartItemCount = (state: any) => {
  const badge = selectCartBadgeCount(state);
  const items = state.cart.cart?.items;
  if (!items?.length) {
    return badge;
  }
  const fromCart = countCartTotalQuantity(items);
  if (state.cart.status === 'loading') {
    return Math.max(fromCart, badge);
  }
  return fromCart;
};
export const selectCartSubtotal = (state: any) =>
  (state.cart.cart?.items || []).reduce((sum: number, item: CartItem) => sum + Number(item.totalPrice), 0);
export const selectCartTotal = (state: any) =>
  (state.cart.cart?.items || []).reduce((sum: number, item: CartItem) => sum + Number(item.totalPrice), 0);

export default cartSlice.reducer;
